<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import PhoneVerification from './PhoneVerification.vue';
import CoexistenceHistorySync from './CoexistenceHistorySync.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useLocale } from 'shared/composables/useLocale';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

import ButtonV4 from 'next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Select from 'next/select/Select.vue';
import Dialog from 'next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inboxId: {
    type: [Number, String],
    default: null,
  },
  healthData: {
    type: Object,
    default: null,
  },
  healthError: {
    type: Object,
    default: null,
  },
  isEmbeddedSignup: {
    type: Boolean,
    default: false,
  },
  isRegisteringWebhook: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['registerWebhook', 'goToConfiguration', 'verified']);

const { t, te } = useI18n();
const { resolvedLocale } = useLocale();

const QUALITY_COLORS = {
  GREEN: 'text-n-teal-11',
  YELLOW: 'text-n-amber-11',
  RED: 'text-n-ruby-11',
  UNKNOWN: 'text-n-slate-12',
};

const STATUS_COLORS = {
  APPROVED: 'text-n-teal-11',
  CONNECTED: 'text-n-teal-11',
  VERIFIED: 'text-n-teal-11',
  STANDARD: 'text-n-teal-11',
  ACTIVE: 'text-n-teal-11',
  PENDING_REVIEW: 'text-n-amber-11',
  AVAILABLE_WITHOUT_REVIEW: 'text-n-slate-11',
  EXPIRED: 'text-n-amber-11',
  FLAGGED: 'text-n-amber-11',
  RESTRICTED: 'text-n-ruby-9',
  BANNED: 'text-n-ruby-9',
  DISCONNECTED: 'text-n-ruby-9',
  REJECTED: 'text-n-ruby-9',
  DECLINED: 'text-n-ruby-9',
};

const MODE_COLORS = {
  LIVE: 'text-n-teal-11',
  SANDBOX: 'text-n-slate-11',
};

const formatStatusDisplay = status => {
  const translationKey = `INBOX_MGMT.ACCOUNT_HEALTH.VALUES.STATUSES.${status}`;

  if (te(translationKey)) {
    return t(translationKey);
  }

  return status
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const STATUSES = [
  'APPROVED',
  'AVAILABLE_WITHOUT_REVIEW',
  'PENDING_REVIEW',
  'REJECTED',
  'DECLINED',
  'EXPIRED',
];

const getDisplayNameStatusDescription = status =>
  t(
    `INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.DESCRIPTIONS.${
      STATUSES.includes(status) ? status : 'UNKNOWN'
    }`
  );

const formatDateTime = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(resolvedLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const businessProfile = computed(
  () => props.healthData?.business_profile || null
);

const businessProfileName = computed(
  () =>
    props.healthData?.verified_name ||
    props.healthData?.display_phone_number ||
    t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.NOT_AVAILABLE')
);

const businessProfileDetails = computed(() => {
  if (!businessProfile.value) return [];

  const notAvailable = t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.NOT_AVAILABLE');
  const websites = businessProfile.value.websites?.filter(Boolean) || [];

  return [
    {
      key: 'description',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_DESCRIPTION.LABEL'),
      value: businessProfile.value.description || notAvailable,
      tooltip: t(
        'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_DESCRIPTION.TOOLTIP'
      ),
      wide: true,
    },
    {
      key: 'address',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_ADDRESS.LABEL'),
      value: businessProfile.value.address || notAvailable,
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_ADDRESS.TOOLTIP'),
    },
    {
      key: 'email',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_EMAIL.LABEL'),
      value: businessProfile.value.email || notAvailable,
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_EMAIL.TOOLTIP'),
    },
    {
      key: 'websites',
      label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_WEBSITES.LABEL'),
      value: websites.length ? websites.join('\n') : notAvailable,
      tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PROFILE_WEBSITES.TOOLTIP'),
      wide: true,
    },
  ];
});

const healthSections = computed(() => {
  if (!props.healthData) {
    return [];
  }

  const {
    id: phoneNumberId,
    display_phone_number: displayPhoneNumber,
    verified_name: verifiedName,
    name_status: nameStatus,
    quality_rating: qualityRating,
    messaging_limit_tier: messagingLimitTier,
    account_mode: accountMode,
    status: phoneNumberStatus,
    code_verification_status: codeVerificationStatus,
    throughput_level: throughputLevel,
    last_onboarded_time: lastOnboardedTime,
    is_on_biz_app: isOnBizApp,
    platform_type: platformType,
    business_account_id: businessAccountId,
    business_account_name: businessAccountName,
    business_portfolio_id: businessPortfolioId,
    business_portfolio_name: businessPortfolioName,
  } = props.healthData;

  const notAvailable = t('INBOX_MGMT.ACCOUNT_HEALTH.VALUES.NOT_AVAILABLE');

  return [
    {
      key: 'phoneNumber',
      icon: 'i-lucide-smartphone',
      title: t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.PHONE_NUMBER'),
      items: [
        {
          key: 'displayPhoneNumber',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.LABEL'
          ),
          value: displayPhoneNumber || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_PHONE_NUMBER.TOOLTIP'
          ),
        },
        {
          key: 'verifiedName',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.LABEL'),
          value: verifiedName || notAvailable,
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.VERIFIED_NAME.TOOLTIP'),
        },
        {
          key: 'displayNameStatus',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.LABEL'
          ),
          value: nameStatus || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.DISPLAY_NAME_STATUS.TOOLTIP'
          ),
          description: getDisplayNameStatusDescription(nameStatus || 'UNKNOWN'),
          type: 'status',
          wide: true,
        },
        {
          key: 'phoneNumberId',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_ID.LABEL'),
          value: phoneNumberId || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_ID.TOOLTIP'
          ),
        },
        {
          key: 'coexistence',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.COEXISTENCE.LABEL'),
          value: 'ACTIVE',
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.COEXISTENCE.TOOLTIP'),
          type: 'status',
          show: isOnBizApp === true && platformType === 'CLOUD_API',
        },
        {
          key: 'lastOnboardedTime',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.LAST_ONBOARDED_TIME.LABEL'
          ),
          value: lastOnboardedTime
            ? formatDateTime(lastOnboardedTime)
            : notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.LAST_ONBOARDED_TIME.TOOLTIP'
          ),
          show: Boolean(lastOnboardedTime),
        },
      ],
    },
    {
      key: 'healthAndCapacity',
      icon: 'i-lucide-heart-pulse',
      title: t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.HEALTH_AND_CAPACITY'),
      items: [
        {
          key: 'phoneNumberStatus',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_STATUS.LABEL'
          ),
          value: phoneNumberStatus || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.PHONE_NUMBER_STATUS.TOOLTIP'
          ),
          type: 'status',
        },
        {
          key: 'qualityRating',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.LABEL'),
          value: qualityRating || 'UNKNOWN',
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.QUALITY_RATING.TOOLTIP'),
          type: 'quality',
        },
        {
          key: 'messagingLimitTier',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.LABEL'
          ),
          value: messagingLimitTier || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.TOOLTIP'
          ),
          description: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.MESSAGING_LIMIT_TIER.DESCRIPTION'
          ),
          type: 'tier',
        },
        {
          key: 'accountMode',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.LABEL'),
          value: accountMode || 'UNKNOWN',
          tooltip: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.ACCOUNT_MODE.TOOLTIP'),
          type: 'mode',
        },
        {
          key: 'codeVerificationStatus',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.CODE_VERIFICATION_STATUS.LABEL'
          ),
          value: codeVerificationStatus || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.CODE_VERIFICATION_STATUS.TOOLTIP'
          ),
          type: 'status',
        },
        {
          key: 'throughputLevel',
          label: t('INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.THROUGHPUT_LEVEL.LABEL'),
          value: throughputLevel || 'UNKNOWN',
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.THROUGHPUT_LEVEL.TOOLTIP'
          ),
          type: 'status',
        },
      ],
    },
    {
      key: 'businessAccount',
      icon: 'i-lucide-building-2',
      title: t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.BUSINESS_ACCOUNT'),
      items: [
        {
          key: 'businessAccountName',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_NAME.LABEL'
          ),
          value: businessAccountName || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_NAME.TOOLTIP'
          ),
        },
        {
          key: 'businessAccountId',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_ID.LABEL'
          ),
          value: businessAccountId || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_ACCOUNT_ID.TOOLTIP'
          ),
        },
        {
          key: 'businessPortfolioName',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_NAME.LABEL'
          ),
          value: businessPortfolioName || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_NAME.TOOLTIP'
          ),
        },
        {
          key: 'businessPortfolioId',
          label: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_ID.LABEL'
          ),
          value: businessPortfolioId || notAvailable,
          tooltip: t(
            'INBOX_MGMT.ACCOUNT_HEALTH.FIELDS.BUSINESS_PORTFOLIO_ID.TOOLTIP'
          ),
        },
      ],
    },
  ].map(section => ({
    ...section,
    items: section.items.filter(item => item.show !== false),
  }));
});

const errorState = computed(() => {
  if (!props.healthError) return null;

  if (props.healthError.type === 'authorization') {
    return {
      title: props.isEmbeddedSignup
        ? t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.EMBEDDED_TITLE')
        : t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.MANUAL_TITLE'),
      description: props.isEmbeddedSignup
        ? t(
            'INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.EMBEDDED_DESCRIPTION'
          )
        : t(
            'INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.AUTHORIZATION.MANUAL_DESCRIPTION'
          ),
      showConfigurationAction: true,
    };
  }

  return {
    title: t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.GENERIC_TITLE'),
    description: t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.GENERIC_DESCRIPTION'),
    showConfigurationAction: false,
  };
});

const handleGoToSettings = () => {
  const {
    business_account_id: businessAccountId,
    business_portfolio_id: businessPortfolioId,
  } = props.healthData || {};

  if (businessPortfolioId && businessAccountId) {
    const whatsappBusinessUrl = new URL(
      'https://business.facebook.com/latest/whatsapp_manager/phone_numbers/'
    );
    whatsappBusinessUrl.searchParams.set('business_id', businessPortfolioId);
    whatsappBusinessUrl.searchParams.set('asset_id', businessAccountId);
    window.open(whatsappBusinessUrl.toString(), '_blank');
  } else {
    const fallbackUrl = 'https://business.facebook.com/';
    window.open(fallbackUrl, '_blank');
  }
};

const getQualityRatingTextColor = rating =>
  QUALITY_COLORS[rating] || QUALITY_COLORS.UNKNOWN;

const formatTierDisplay = tier => {
  const translationKey = `INBOX_MGMT.ACCOUNT_HEALTH.VALUES.TIERS.${tier}`;

  return te(translationKey) ? t(translationKey) : formatStatusDisplay(tier);
};

const formatModeDisplay = mode => {
  const translationKey = `INBOX_MGMT.ACCOUNT_HEALTH.VALUES.MODES.${mode}`;

  return te(translationKey) ? t(translationKey) : formatStatusDisplay(mode);
};

const getModeStatusTextColor = mode => MODE_COLORS[mode] || 'text-n-slate-12';

const getStatusTextColor = status => STATUS_COLORS[status] || 'text-n-slate-12';

const showWebhookSection = computed(
  () => props.healthData?.webhook_configuration !== undefined
);

// Phone-level override takes precedence over WABA-level (application), so prefer it.
const webhookUrl = computed(
  () =>
    props.healthData?.webhook_configuration?.phone_number ||
    props.healthData?.webhook_configuration?.whatsapp_business_account ||
    props.healthData?.webhook_configuration?.application
);

const webhookConfigured = computed(() => !!webhookUrl.value);

const isCoexistence = computed(
  () =>
    props.healthData?.is_on_biz_app === true &&
    props.healthData?.platform_type === 'CLOUD_API'
);

const webhookActionLabel = computed(() =>
  isCoexistence.value
    ? t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REPAIR_COEXISTENCE_BUTTON')
    : t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REGISTER_BUTTON')
);

const webhookUrlMismatch = computed(
  () =>
    webhookConfigured.value &&
    webhookUrl.value !== props.healthData?.expected_webhook_url
);

const handleRegisterWebhook = () => {
  emit('registerWebhook', { repairCoexistence: isCoexistence.value });
};

const handleGoToConfiguration = () => {
  emit('goToConfiguration');
};

const handleCopyWebhookUrl = async url => {
  await copyTextToClipboard(url);
  useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_SUCCESS'));
};

const initialMessage = ref('');
const messageLinks = ref([]);
const selectedMessageLink = ref('');
const messageQrCode = ref('');
const isLoadingMessageLinks = ref(false);
const isCreatingMessageLink = ref(false);
const messageLink = computed(
  () =>
    messageLinks.value.find(
      link => link.deep_link_url === selectedMessageLink.value
    ) || null
);

const deleteLinkDialog = ref(null);
const pendingDeleteLink = ref(null);
const isDeletingMessageLink = ref(false);
const confirmDeleteMessageLink = () => {
  pendingDeleteLink.value = messageLink.value;
  deleteLinkDialog.value?.open();
};
const deleteMessageLink = async () => {
  const link = pendingDeleteLink.value;
  if (!link?.code || isDeletingMessageLink.value) return;
  isDeletingMessageLink.value = true;
  try {
    await InboxHealthAPI.deleteMessageLink(props.inboxId, link.code);
    messageLinks.value = messageLinks.value.filter(
      item => item.code !== link.code
    );
    if (selectedMessageLink.value === link.deep_link_url) {
      selectedMessageLink.value = messageLinks.value[0]?.deep_link_url || '';
    }
    deleteLinkDialog.value?.close();
    pendingDeleteLink.value = null;
    useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DELETE_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DELETE_ERROR'));
  } finally {
    isDeletingMessageLink.value = false;
  }
};

watch(
  () => [
    props.healthData?.verified_name,
    props.healthData?.display_phone_number,
  ],
  ([name]) => {
    if (initialMessage.value || !props.healthData) return;

    initialMessage.value = t(
      'INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DEFAULT_MESSAGE',
      {
        name:
          name || t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.COMPANY_FALLBACK'),
      }
    );
  },
  { immediate: true }
);

watch(
  () => messageLink.value?.deep_link_url,
  async link => {
    messageQrCode.value = link
      ? await QRCode.toDataURL(link, { width: 240, margin: 1 })
      : '';
  },
  { immediate: true }
);

const fetchMessageLinks = async () => {
  if (!props.inboxId) return;

  try {
    isLoadingMessageLinks.value = true;
    const response = await InboxHealthAPI.getMessageLinks(props.inboxId);
    messageLinks.value = response.data?.data || [];
    selectedMessageLink.value = messageLinks.value[0]?.deep_link_url || '';
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.LOAD_ERROR')
    );
  } finally {
    isLoadingMessageLinks.value = false;
  }
};

watch(() => props.inboxId, fetchMessageLinks, { immediate: true });

const createMessageLink = async () => {
  const message = initialMessage.value.trim();
  if (!props.inboxId || !message) return;

  try {
    isCreatingMessageLink.value = true;
    const response = await InboxHealthAPI.createMessageLink(
      props.inboxId,
      message
    );
    const createdLink = response.data?.data;
    if (createdLink?.deep_link_url) {
      messageLinks.value = [
        createdLink,
        ...messageLinks.value.filter(
          link => link.deep_link_url !== createdLink.deep_link_url
        ),
      ];
      selectedMessageLink.value = createdLink.deep_link_url;
    }
    useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.CREATE_ERROR')
    );
  } finally {
    isCreatingMessageLink.value = false;
  }
};

const copyMessageLink = async () => {
  await copyTextToClipboard(messageLink.value.deep_link_url);
  useAlert(t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.COPY_SUCCESS'));
};

const openMessageLink = () => {
  window.open(messageLink.value.deep_link_url, '_blank', 'noopener,noreferrer');
};

const downloadMessageQrCode = () => {
  const anchor = document.createElement('a');
  anchor.href = messageQrCode.value;
  anchor.download = `whatsapp-${messageLink.value.code || 'mensagem'}.png`;
  anchor.click();
};
</script>

<template>
  <Dialog
    ref="deleteLinkDialog"
    type="alert"
    :title="t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DELETE_TITLE')"
    :description="
      t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DELETE_DESCRIPTION')
    "
    :confirm-button-label="
      t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DELETE_BUTTON')
    "
    :is-loading="isDeletingMessageLink"
    :disable-confirm-button="isDeletingMessageLink"
    :close-on-click-outside="!isDeletingMessageLink"
    @confirm="deleteMessageLink"
  >
    <p class="break-all text-sm text-n-slate-11">
      {{ pendingDeleteLink?.deep_link_url }}
    </p>
  </Dialog>
  <div class="mx-5 sm:mx-6">
    <div class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1">
      <div
        class="flex flex-col gap-5 justify-between items-start px-5 py-5 border-b border-n-weak md:flex-row md:items-center"
      >
        <div class="flex gap-3 items-start min-w-0">
          <span
            class="flex flex-shrink-0 justify-center items-center w-10 h-10 rounded-xl bg-n-teal-3 text-n-teal-11"
          >
            <Icon icon="i-lucide-heart-pulse" class="w-5 h-5" />
          </span>
          <div class="min-w-0">
            <h2 class="text-heading-3 text-n-slate-12">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.TITLE') }}
            </h2>
            <p class="mt-1 text-body-main text-n-slate-11">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.DESCRIPTION') }}
            </p>
          </div>
        </div>
        <ButtonV4
          sm
          solid
          blue
          class="flex-shrink-0"
          @click="handleGoToSettings"
        >
          {{ t('INBOX_MGMT.ACCOUNT_HEALTH.GO_TO_SETTINGS') }}
        </ButtonV4>
      </div>

      <div v-if="healthData && !errorState" class="p-5 space-y-5">
        <section
          v-if="businessProfile"
          class="overflow-hidden rounded-xl border border-n-weak"
        >
          <div
            class="flex gap-2.5 items-center px-4 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <Icon icon="i-lucide-building-2" class="w-4 h-4 text-n-slate-10" />
            <h3 class="text-heading-4 text-n-slate-12">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.SECTIONS.BUSINESS_PROFILE') }}
            </h3>
          </div>
          <div class="p-4 space-y-4">
            <div class="flex items-center gap-4">
              <Avatar
                :src="businessProfile.profile_picture_url"
                :name="businessProfileName"
                :size="64"
                rounded-full
              />
              <div class="min-w-0">
                <p class="text-heading-4 break-words text-n-slate-12">
                  {{ businessProfileName }}
                </p>
                <p
                  v-if="healthData.display_phone_number"
                  class="text-body-main text-n-slate-10"
                >
                  {{ healthData.display_phone_number }}
                </p>
                <div class="flex flex-wrap gap-2 items-center mt-2">
                  <span
                    v-if="
                      businessProfile.vertical &&
                      businessProfile.vertical !== 'OTHER'
                    "
                    class="inline-flex items-center px-2 py-0.5 min-h-6 text-label-small rounded-md bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ formatStatusDisplay(businessProfile.vertical) }}
                  </span>
                  <span
                    v-if="businessProfile.about"
                    class="text-label break-words text-n-slate-11"
                  >
                    {{ businessProfile.about }}
                  </span>
                </div>
              </div>
            </div>
            <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div
                v-for="profileItem in businessProfileDetails"
                :key="profileItem.key"
                class="p-4 rounded-lg border border-n-weak bg-n-solid-1"
                :class="{ 'sm:col-span-2': profileItem.wide }"
              >
                <dt class="text-body-main font-medium text-n-slate-11">
                  {{ profileItem.label }}
                </dt>
                <dd
                  class="mt-2 text-label whitespace-pre-line break-words text-n-slate-12"
                >
                  {{ profileItem.value }}
                </dd>
              </div>
            </dl>
          </div>
        </section>

        <section
          v-if="inboxId"
          class="overflow-hidden rounded-xl border border-n-weak"
        >
          <div
            class="flex gap-2.5 items-center px-4 py-3 border-b border-n-weak bg-n-alpha-1"
          >
            <Icon
              icon="i-lucide-message-circle"
              class="w-4 h-4 text-n-teal-10"
            />
            <div>
              <h3 class="text-heading-4 text-n-slate-12">
                {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.TITLE') }}
              </h3>
              <p class="text-label-small text-n-slate-10">
                {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid gap-5 p-4 lg:grid-cols-[minmax(0,1fr)_220px]">
            <div class="min-w-0 space-y-4">
              <TextArea
                id="whatsapp-link-message"
                v-model="initialMessage"
                :label="
                  t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.MESSAGE_LABEL')
                "
                :max-length="140"
                show-character-count
                auto-height
                resize
                min-height="5rem"
              />
              <ButtonV4
                type="button"
                sm
                solid
                blue
                :is-loading="isCreatingMessageLink"
                :disabled="!initialMessage.trim() || isCreatingMessageLink"
                @click="createMessageLink"
              >
                {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.CREATE_BUTTON') }}
              </ButtonV4>
              <label v-if="messageLinks.length" class="block">
                <span class="text-body-main font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.SAVED_LINKS') }}
                </span>
                <Select
                  v-model="selectedMessageLink"
                  class="mt-1.5 !w-full [&_select]:w-full"
                  :aria-label="
                    t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.SAVED_LINKS')
                  "
                  :options="
                    messageLinks.map(link => ({
                      value: link.deep_link_url,
                      label: link.prefilled_message || link.deep_link_url,
                    }))
                  "
                />
              </label>
              <p
                v-else-if="!isLoadingMessageLinks"
                class="text-body-main text-n-slate-10"
              >
                {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.EMPTY') }}
              </p>
              <div
                v-if="messageLink"
                class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
              >
                <span class="text-label-small text-n-slate-10">
                  {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.LINK_LABEL') }}
                </span>
                <p class="mt-1 break-all font-mono text-label text-n-slate-12">
                  {{ messageLink.deep_link_url }}
                </p>
              </div>
              <div v-if="messageLink" class="flex flex-wrap gap-2">
                <ButtonV4
                  v-if="messageLink.code"
                  type="button"
                  sm
                  ruby
                  faded
                  icon="i-lucide-trash-2"
                  :label="
                    t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DELETE_BUTTON')
                  "
                  :disabled="isDeletingMessageLink"
                  @click="confirmDeleteMessageLink"
                />
                <ButtonV4
                  type="button"
                  sm
                  outline
                  blue
                  @click="openMessageLink"
                >
                  {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.OPEN_BUTTON') }}
                </ButtonV4>
                <ButtonV4
                  type="button"
                  sm
                  outline
                  slate
                  @click="copyMessageLink"
                >
                  {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.COPY_BUTTON') }}
                </ButtonV4>
              </div>
            </div>
            <div
              v-if="messageLink"
              class="flex flex-col items-center justify-center rounded-xl bg-white p-3"
            >
              <img
                v-if="messageQrCode"
                :src="messageQrCode"
                :alt="t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.QR_ALT')"
                class="size-40"
              />
              <ButtonV4
                v-if="messageQrCode"
                type="button"
                sm
                ghost
                slate
                @click="downloadMessageQrCode"
              >
                {{ t('INBOX_MGMT.ACCOUNT_HEALTH.MESSAGE_LINK.DOWNLOAD_QR') }}
              </ButtonV4>
            </div>
          </div>
        </section>

        <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <section
            v-for="section in healthSections"
            :key="section.key"
            class="overflow-hidden rounded-xl border border-n-weak"
            :class="{ 'xl:col-span-2': section.key === 'businessAccount' }"
          >
            <div
              class="flex gap-2.5 items-center px-4 py-3 border-b border-n-weak bg-n-alpha-1"
            >
              <Icon :icon="section.icon" class="w-4 h-4 text-n-slate-10" />
              <h3 class="text-heading-4 text-n-slate-12">
                {{ section.title }}
              </h3>
            </div>
            <dl class="divide-y divide-n-weak">
              <div
                v-for="item in section.items"
                :key="item.key"
                class="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
              >
                <dt class="flex min-w-0 gap-2 items-center text-n-slate-11">
                  <span class="min-w-0 break-words text-body-main">
                    {{ item.label }}
                  </span>
                  <Icon
                    v-tooltip.top="item.tooltip"
                    icon="i-lucide-info"
                    class="flex-shrink-0 w-3.5 h-3.5 cursor-help text-n-slate-9"
                  />
                </dt>
                <dd class="flex flex-shrink-0 items-center sm:max-w-[55%]">
                  <span
                    v-if="item.type === 'quality'"
                    class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-full bg-n-alpha-2"
                    :class="getQualityRatingTextColor(item.value)"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-current" />
                    {{ item.value }}
                  </span>
                  <span
                    v-else-if="item.type === 'status'"
                    class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-full bg-n-alpha-2"
                    :class="getStatusTextColor(item.value)"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-current" />
                    {{ formatStatusDisplay(item.value) }}
                  </span>
                  <span
                    v-else-if="item.type === 'mode'"
                    class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-full bg-n-alpha-2"
                    :class="getModeStatusTextColor(item.value)"
                  >
                    <span class="w-1.5 h-1.5 rounded-full bg-current" />
                    {{ formatModeDisplay(item.value) }}
                  </span>
                  <span
                    v-else-if="item.type === 'tier'"
                    class="text-label text-right text-n-slate-12"
                  >
                    {{ formatTierDisplay(item.value) }}
                  </span>
                  <span
                    v-else
                    class="min-w-0 break-all text-label text-right text-n-slate-12"
                  >
                    {{ item.value }}
                  </span>
                </dd>
                <PhoneVerification
                  v-if="item.key === 'codeVerificationStatus' && inboxId"
                  :key="inboxId"
                  :inbox-id="inboxId"
                  :phone="healthData.display_phone_number"
                  :status="healthData.code_verification_status"
                  :coexistence="healthData.is_on_biz_app === true"
                  @verified="emit('verified')"
                />
                <p
                  v-if="item.description"
                  class="w-full text-label-small text-n-slate-10"
                >
                  {{ item.description }}
                </p>
              </div>
            </dl>
          </section>
        </div>

        <CoexistenceHistorySync
          v-if="isCoexistence && inboxId"
          :key="`coexistence-${inboxId}`"
          :inbox-id="inboxId"
        />

        <div
          v-if="showWebhookSection"
          class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex gap-3 items-start min-w-0">
            <span
              class="flex flex-shrink-0 justify-center items-center w-9 h-9 rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              <Icon icon="i-lucide-webhook" class="w-4 h-4" />
            </span>
            <div class="min-w-0 space-y-1.5">
              <div class="flex flex-wrap gap-2 items-center">
                <span class="text-body-main font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.TITLE') }}
                </span>
                <span
                  v-if="webhookConfigured && !webhookUrlMismatch"
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-full bg-n-teal-3 text-n-teal-11"
                >
                  <Icon icon="i-lucide-check-circle" class="w-3.5 h-3.5" />
                  {{
                    t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.CONFIGURED_SUCCESS')
                  }}
                </span>
                <span
                  v-else
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 min-h-6 text-label-small rounded-full bg-n-amber-3 text-n-amber-11"
                >
                  <Icon icon="i-lucide-alert-triangle" class="w-3.5 h-3.5" />
                  {{
                    webhookUrlMismatch
                      ? t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.URL_MISMATCH')
                      : t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.ACTION_REQUIRED')
                  }}
                </span>
              </div>
              <p class="text-body-small text-n-slate-10">
                {{
                  isCoexistence
                    ? t(
                        'INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REPAIR_COEXISTENCE_DESCRIPTION'
                      )
                    : t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.DESCRIPTION')
                }}
              </p>
              <div v-if="webhookConfigured" class="space-y-1.5">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="flex-shrink-0 text-label-small text-n-slate-10">
                    {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.CONFIGURED_URL') }}
                  </span>
                  <span
                    v-tooltip.top="webhookUrl"
                    class="min-w-0 font-mono text-label-small truncate text-n-slate-12"
                  >
                    {{ webhookUrl }}
                  </span>
                  <ButtonV4
                    v-tooltip.top="
                      t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')
                    "
                    type="button"
                    ghost
                    sm
                    slate
                    icon="i-lucide-copy"
                    class="flex-shrink-0"
                    :aria-label="
                      t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')
                    "
                    @click="handleCopyWebhookUrl(webhookUrl)"
                  />
                </div>
                <div
                  v-if="webhookUrlMismatch"
                  class="flex items-center gap-2 min-w-0"
                >
                  <span class="flex-shrink-0 text-label-small text-n-slate-10">
                    {{ t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.EXPECTED_URL') }}
                  </span>
                  <span
                    v-tooltip.top="healthData.expected_webhook_url"
                    class="min-w-0 font-mono text-label-small truncate text-n-slate-12"
                  >
                    {{ healthData.expected_webhook_url }}
                  </span>
                  <ButtonV4
                    v-tooltip.top="
                      t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')
                    "
                    type="button"
                    ghost
                    sm
                    slate
                    icon="i-lucide-copy"
                    class="flex-shrink-0"
                    :aria-label="
                      t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.COPY_URL')
                    "
                    @click="
                      handleCopyWebhookUrl(healthData.expected_webhook_url)
                    "
                  />
                </div>
              </div>
            </div>
          </div>
          <ButtonV4
            sm
            solid
            blue
            :loading="isRegisteringWebhook"
            :disabled="isRegisteringWebhook"
            class="flex-shrink-0"
            @click="handleRegisterWebhook"
          >
            {{ webhookActionLabel }}
          </ButtonV4>
        </div>
      </div>

      <div v-else-if="errorState" class="p-5">
        <div
          class="flex flex-col gap-4 p-5 rounded-xl border border-n-amber-5 bg-n-amber-2"
        >
          <div class="flex gap-3 items-start">
            <span
              class="flex flex-shrink-0 justify-center items-center w-9 h-9 rounded-lg bg-n-amber-3 text-n-amber-11"
            >
              <Icon icon="i-lucide-alert-triangle" class="w-4 h-4" />
            </span>
            <div class="min-w-0">
              <h3 class="text-heading-4 text-n-slate-12">
                {{ errorState.title }}
              </h3>
              <p class="mt-1 text-body-main text-n-slate-11">
                {{ errorState.description }}
              </p>
            </div>
          </div>
          <div
            v-if="healthError.message"
            class="p-3 rounded-lg bg-n-alpha-1 text-label-small text-n-slate-11"
          >
            <p class="break-words">{{ healthError.message }}</p>
          </div>
          <div v-if="errorState.showConfigurationAction">
            <ButtonV4 sm solid blue @click="handleGoToConfiguration">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.ERRORS.GO_TO_CONFIGURATION') }}
            </ButtonV4>
          </div>
        </div>
      </div>

      <div v-else class="pt-8">
        <div
          class="flex justify-center items-center p-8 text-center text-n-slate-11"
        >
          <div>
            <Icon icon="i-lucide-activity" class="mb-2 w-8 h-8" />
            <p class="text-body-main text-n-slate-11">
              {{ t('INBOX_MGMT.ACCOUNT_HEALTH.NO_DATA') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
